"use client"

import * as React from "react"
import { cn } from '../../../../lib/utils'
import { gridItemVariants } from "../constants"
import { GridItemContext, useGridView, useElementHeight } from "../use-grid-view"
import type { GridItemProps, GridItemData } from "../types"

function GridItemInner<TData extends GridItemData>(
  {
    className,
    variant,
    interactive,
    item,
    index = 0,
    onClick,
    children,
    ...props
  }: GridItemProps<TData>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const internalRef = React.useRef<HTMLDivElement>(null)
  const combinedRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      ;(internalRef as React.MutableRefObject<HTMLDivElement | null>).current = node
      if (typeof ref === "function") {
        ref(node)
      } else if (ref) {
        ref.current = node
      }
    },
    [ref]
  )

  const gridContext = useGridView<TData>()
  const { getItemKey, reportHeight } = gridContext

  const itemKey = getItemKey(item, index)

  // Context value for this item
  const contextValue = React.useMemo(
    () => ({ item, index }),
    [item, index]
  )

  // Determine if interactive
  const isInteractive = interactive ?? !!onClick

  // Report height when it changes
  const handleHeightChange = React.useCallback(
    (height: number) => {
      reportHeight(itemKey, height)
    },
    [reportHeight, itemKey]
  )

  useElementHeight(internalRef, handleHeightChange)

  // Handle click
  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      onClick?.(item, event)
    },
    [onClick, item]
  )

  return (
    <GridItemContext.Provider value={contextValue}>
      <div
        ref={combinedRef}
        className={cn(
          gridItemVariants({ variant, interactive: isInteractive }),
          className
        )}
        onClick={isInteractive ? handleClick : undefined}
        {...props}
      >
        {children || (
          <div className="p-4">
            <p className="text-sm font-medium">{item.id}</p>
          </div>
        )}
      </div>
    </GridItemContext.Provider>
  )
}

export const GridItem = React.forwardRef(GridItemInner) as <
  TData extends GridItemData
>(
  props: GridItemProps<TData> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => React.ReactElement

;(GridItem as React.FC).displayName = "GridItem"
